import { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { ClipboardX, Loader2 } from 'lucide-react';
import {
  scanPendingDrafts,
  loadDraftSnapshot,
  clearDraftByKey,
  PendingDraftInfo,
} from '@/hooks/use-draft';
import {
  loadMediaForDraft,
  deleteMediaForDraft,
  restoreMediaIntoFormData,
} from '@/lib/draft-media-db';
import { InspectionFormData } from '@/types/inspection-form';
import { createInitialFormData } from '@/features/inspection-form/utils/initial-data';
import { useSubmitInspection } from '@/hooks/use-submit-inspection';
import { toast } from '@/lib/toast';
import { formatTimestamp } from '@/lib/date';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface RowStatus {
  state: 'pending' | 'submitting' | 'failed';
}

export function UnsubmittedReports() {
  const [drafts, setDrafts] = useState<PendingDraftInfo[]>([]);
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});
  const { submit } = useSubmitInspection();

  const refresh = useCallback(() => {
    setDrafts(scanPendingDrafts());
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    return () => {
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
    };
  }, [refresh]);

  const submitDraft = useCallback(
    async (info: PendingDraftInfo) => {
      const { draftKey, taskMeta } = info;
      setRowStatus((prev) => ({ ...prev, [draftKey]: { state: 'submitting' } }));

      try {
        const snapshot = loadDraftSnapshot<InspectionFormData>(draftKey);
        if (!snapshot) throw new Error('Draft not found');

        const base = createInitialFormData();
        const partial = { ...base, ...snapshot.formData };
        const storedMedia = await loadMediaForDraft(draftKey);
        const fullFormData = restoreMediaIntoFormData(partial, storedMedia);

        await submit(fullFormData, taskMeta.taskId, taskMeta.propertyId, {});

        clearDraftByKey(draftKey);
        await deleteMediaForDraft(draftKey);

        setDrafts((prev) => prev.filter((d) => d.draftKey !== draftKey));
        setRowStatus((prev) => {
          const next = { ...prev };
          delete next[draftKey];
          return next;
        });
        toast.success('Report submitted successfully.');
      } catch {
        setRowStatus((prev) => ({ ...prev, [draftKey]: { state: 'failed' } }));
        toast.error('Submission failed. Please try again when online.');
      }
    },
    [submit],
  );

  const submitAll = useCallback(async () => {
    for (const draft of drafts) {
      if (rowStatus[draft.draftKey]?.state === 'submitting') continue;
      await submitDraft(draft);
    }
  }, [drafts, rowStatus, submitDraft]);

  // Auto-submit all pending when online
  useEffect(() => {
    async function handleOnline() {
      for (const draft of scanPendingDrafts()) {
        await submitDraft(draft);
      }
    }
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [submitDraft]);

  if (drafts.length === 0) return null;

  return (
    <div id="unsubmitted-reports">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Unsubmitted Reports</h2>
          <p className="text-sm text-muted-foreground">
            Saved while offline — submit automatically when you reconnect.
          </p>
        </div>
        {drafts.length > 1 && (
          <Button
            variant="action"
            size="sm"
            onClick={submitAll}
            disabled={Object.values(rowStatus).some((s) => s.state === 'submitting')}
          >
            Submit All
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Task</TableHead>
              <TableHead>Saved</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {drafts.map((info) => {
              const status = rowStatus[info.draftKey];
              const isSubmitting = status?.state === 'submitting';
              return (
                <TableRow key={info.draftKey}>
                  <TableCell>
                    <div className="font-medium">{info.taskMeta.taskTitle}</div>
                    {/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic route interpolation */}
                    <Link
                      to={`/dashboard/task/${info.taskMeta.taskId}` as any}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      View task →
                    </Link>
                    {/* eslint-enable @typescript-eslint/no-explicit-any */}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatTimestamp(new Date(info.savedAt))}
                  </TableCell>
                  <TableCell>
                    {isSubmitting ? (
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Submitting…
                      </span>
                    ) : status?.state === 'failed' ? (
                      <span className="text-sm text-destructive">Failed</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">Pending</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isSubmitting || !navigator.onLine}
                      onClick={() => submitDraft(info)}
                    >
                      Submit now
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {drafts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center space-y-2">
          <ClipboardX className="h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No pending reports.</p>
        </div>
      )}
    </div>
  );
}
